import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { cn } from "../../utils/tailwindUtils";
import { Plus, Send, BarChart2, Loader2, Trash2, RefreshCw } from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { EMAIL_CAMPAIGNS_URL, EMAIL_CAMPAIGN_START_URL } from "../../constants/api";

const STATUS_COLORS = {
    draft:      'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    scheduled:  'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    running:    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    paused:     'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
    completed:  'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
    cancelled:  'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    failed:     'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

const pct = (num, den) => den > 0 ? `${Math.round(num / den * 100)}%` : '—';

export default function EmailCampaignList() {
    const navigate = useNavigate();
    const { fetch, create, remove } = useApiMethods();
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(EMAIL_CAMPAIGNS_URL);
            if (res?.status === 'success') setCampaigns(res.data || []);
            else ToastMsg("error", res?.message || "Failed to load campaigns");
        } catch {
            ToastMsg("error", "Failed to load campaigns");
        }
        setLoading(false);
    }, [fetch]);

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const start = async (c) => {
        if (!window.confirm(`Start "${c.name}" now? This sends to the whole audience.`)) return;
        setBusyId(c.id);
        try {
            const res = await create(EMAIL_CAMPAIGN_START_URL(c.id), {});
            if (res?.status === 'success') { ToastMsg("success", "Campaign started"); load(); }
            else ToastMsg("error", res?.message || "Could not start campaign");
        } catch {
            ToastMsg("error", "Could not start campaign");
        }
        setBusyId(null);
    };

    const destroy = async (c) => {
        if (!window.confirm(`Delete "${c.name}"?`)) return;
        try {
            const res = await remove(`${EMAIL_CAMPAIGNS_URL}?id=${c.id}`);
            if (res?.status === 'success') { ToastMsg("success", "Campaign deleted"); load(); }
            else ToastMsg("error", res?.message || "Could not delete campaign");
        } catch {
            ToastMsg("error", "Could not delete campaign");
        }
    };

    return (
        <div className="p-6 space-y-6">
            <ContainerCard>
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Email Campaigns</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                            Bulk email with open and click tracking
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={load} disabled={loading}
                                className="flex items-center gap-1 text-xs">
                            <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} /> Refresh
                        </Button>
                        <Button onClick={() => navigate('/email/campaigns/new')}
                                className="flex items-center gap-2 text-sm">
                            <Plus className="h-4 w-4" /> New Campaign
                        </Button>
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                                    <th className="pb-2 font-medium">Name</th>
                                    <th className="pb-2 font-medium">From</th>
                                    <th className="pb-2 font-medium">Status</th>
                                    <th className="pb-2 font-medium">Recipients</th>
                                    <th className="pb-2 font-medium">Sent</th>
                                    <th className="pb-2 font-medium">Opened</th>
                                    <th className="pb-2 font-medium">Clicked</th>
                                    <th className="pb-2 font-medium">Bounced</th>
                                    <th className="pb-2 font-medium">Failed</th>
                                    <th className="pb-2 font-medium">Scheduled</th>
                                    <th className="pb-2 font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {campaigns.map(c => (
                                    <tr key={c.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                        <td className="py-3 font-medium text-gray-800 dark:text-gray-100">{c.name}</td>
                                        <td className="py-3 text-gray-600 dark:text-gray-400 text-xs">{c.account_name}</td>
                                        <td className="py-3">
                                            <span className={cn(
                                                'inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full',
                                                STATUS_COLORS[c.status] || STATUS_COLORS.draft
                                            )}>
                                                {c.status === 'running' && (
                                                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse inline-block" />
                                                )}
                                                {c.status}
                                            </span>
                                        </td>
                                        <td className="py-3 text-gray-600 dark:text-gray-400">{c.total_recipients}</td>
                                        <td className="py-3 text-gray-600 dark:text-gray-400">{c.sent_count}</td>
                                        <td className="py-3 text-gray-600 dark:text-gray-400">
                                            {c.opened_count} <span className="text-xs text-gray-400">({pct(c.opened_count, c.sent_count)})</span>
                                        </td>
                                        <td className="py-3 text-gray-600 dark:text-gray-400">
                                            {c.clicked_count} <span className="text-xs text-gray-400">({pct(c.clicked_count, c.sent_count)})</span>
                                        </td>
                                        <td className="py-3 text-amber-600 dark:text-amber-500">{c.bounced_count}</td>
                                        <td className="py-3 text-red-500">{c.failed_count}</td>
                                        <td className="py-3 text-gray-500 text-xs">
                                            {c.schedule_at ? new Date(c.schedule_at).toLocaleString() : '—'}
                                        </td>
                                        <td className="py-3">
                                            <div className="flex items-center gap-2">
                                                <button onClick={() => navigate(`/email/campaigns/${c.id}/report`)}
                                                        className="text-gray-400 hover:text-blue-500" title="View report">
                                                    <BarChart2 className="h-4 w-4" />
                                                </button>
                                                {['draft', 'scheduled', 'paused'].includes(c.status) && (
                                                    <button onClick={() => start(c)} disabled={busyId === c.id}
                                                            className="text-gray-400 hover:text-green-500 disabled:opacity-40"
                                                            title="Send now">
                                                        {busyId === c.id
                                                            ? <Loader2 className="h-4 w-4 animate-spin" />
                                                            : <Send className="h-4 w-4" />}
                                                    </button>
                                                )}
                                                {c.status !== 'running' && (
                                                    <button onClick={() => destroy(c)}
                                                            className="text-gray-400 hover:text-red-500" title="Delete">
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {campaigns.length === 0 && (
                                    <tr>
                                        <td colSpan={11} className="py-8 text-center text-sm text-gray-400">
                                            No campaigns yet. Create your first bulk email campaign.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </ContainerCard>
        </div>
    );
}
